import type { PostHog, Properties } from 'posthog-js';

export const POSTHOG_PROJECT_TOKEN =
  import.meta.env.VITE_POSTHOG_TOKEN?.trim() || 'phc_zxgFtSgQPAjStVUvNvCwLWq8xJwSsYch4uL38a2hKsuY';
export const POSTHOG_API_HOST =
  import.meta.env.VITE_POSTHOG_HOST?.trim() || 'https://us.i.posthog.com';

const analyticsBuildEnabled =
  !import.meta.env.DEV || import.meta.env.VITE_POSTHOG_ENABLE_DEV === '1';

type ConsentContract = Window['kilncatPrivacyConsent'];
type ConsentSnapshot = ReturnType<ConsentContract['snapshot']>;
type ConsentAdapter = NonNullable<Parameters<ConsentContract['attachPostHog']>[0]>;

type ProductEvent = {
  name: string;
  properties?: Properties;
  sendInstantly?: boolean;
};

let posthog: PostHog | null = null;
let loading: Promise<void> | null = null;
let consentContract: ConsentContract | null = null;
let consentSnapshot: ConsentSnapshot | null = null;
let unsubscribeConsent: (() => void) | null = null;
let analyticsUserId: string | null = null;
let identifiedUserId: string | null = null;

function cleanURL(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try {
    const url = new URL(value, window.location.origin);
    return `${url.origin}${url.pathname}`;
  } catch {
    return value.split(/[?#]/, 1)[0];
  }
}

/** Keep OAuth signatures, share fragments, and other URL secrets out of analytics. */
export function redactAnalyticsEvent<T extends { properties?: Record<string, unknown> }>(
  event: T,
): T {
  if (!event.properties) return event;
  for (const property of ['$current_url', '$referrer']) {
    if (property in event.properties)
      event.properties[property] = cleanURL(event.properties[property]);
  }
  return event;
}

export function createPostHogConsentAdapter(client: PostHog): ConsentAdapter {
  return {
    // Every optional product other than replay is disabled in init. These
    // hooks remain explicit so the synchronous consent contract owns the full
    // lifecycle rather than handing it to SDK defaults.
    enableOptionalServices: () => {},
    disableOptionalServices: () => {},
    blockCapturing: () => {
      client.set_config({
        capture_pageview: false,
        cookieless_mode: undefined,
        opt_out_capturing_by_default: true,
        opt_out_persistence_by_default: true,
      });
      client.opt_out_capturing();
      client.clear_opt_in_out_capturing();
    },
    opt_in_capturing: () => {
      client.set_config({
        capture_pageview: 'history_change',
        cookieless_mode: 'on_reject',
      });
      client.opt_in_capturing({ captureEventName: false });
      client.set_config({
        opt_out_capturing_by_default: false,
        opt_out_persistence_by_default: false,
      });
      // on_reject treats a marker-free pending SDK state as non-capturing, so
      // retain PostHog's opt-in marker alongside KilnCat's versioned record.
    },
    // With cookieless_mode="on_reject", PostHog deliberately continues only
    // privacy-preserving, non-persistent event capture after this call.
    opt_out_capturing: () => {
      client.set_config({
        capture_pageview: 'history_change',
        cookieless_mode: 'on_reject',
      });
      // Transition first so PostHog clears persistent identity before the
      // default is changed to make a marker-free pending SDK state cookieless.
      client.opt_out_capturing();
      client.set_config({
        opt_out_capturing_by_default: true,
        opt_out_persistence_by_default: true,
      });
      client.clear_opt_in_out_capturing();
    },
    startSessionRecording: () => client.startSessionRecording(),
    stopSessionRecording: () => client.stopSessionRecording(),
  };
}

function initPostHog(snapshot: ConsentSnapshot, client: PostHog): void {
  client.init(POSTHOG_PROJECT_TOKEN, {
    api_host: POSTHOG_API_HOST,
    ui_host: 'https://us.posthog.com',
    defaults: '2026-05-30',
    autocapture: false,
    rageclick: false,
    capture_pageview: 'history_change',
    capture_pageleave: false,
    capture_dead_clicks: false,
    capture_heatmaps: false,
    capture_exceptions: false,
    capture_performance: false,
    disable_surveys: true,
    disable_surveys_automatic_display: true,
    disable_product_tours: true,
    disable_conversations: true,
    disable_web_experiments: true,
    advanced_disable_feature_flags: true,
    person_profiles: 'identified_only',
    save_campaign_params: false,
    save_referrer: false,
    respect_dnt: true,
    ip: false,
    cookieless_mode: 'on_reject',
    opt_out_capturing_by_default: !snapshot.choices.analytics,
    opt_out_persistence_by_default: !snapshot.choices.analytics,
    disable_session_recording: !snapshot.choices.replay,
    enable_recording_console_log: false,
    session_recording: {
      maskAllInputs: true,
      blockSelector: 'canvas',
    },
    before_send: (event) => (event ? redactAnalyticsEvent(event) : null),
  });
}

async function syncPostHog(snapshot: ConsentSnapshot): Promise<void> {
  consentSnapshot = snapshot;
  if (!snapshot.canLoadPostHog || !analyticsBuildEnabled) return;

  if (posthog) {
    consentContract?.attachPostHog(createPostHogConsentAdapter(posthog));
    syncAnalyticsIdentity();
    return;
  }
  if (loading) return loading;

  loading = import('posthog-js')
    .then(({ default: client }) => {
      // Consent may have changed while the SDK chunk loaded. GPC/DNT still
      // wins before init makes its first request.
      const current = consentContract?.snapshot();
      if (!current?.canLoadPostHog) return;
      initPostHog(current, client);
      posthog = client;
      consentContract?.attachPostHog(createPostHogConsentAdapter(client));
      syncAnalyticsIdentity();
    })
    .catch((error) => {
      console.warn('[analytics] PostHog could not be loaded', error);
    })
    .finally(() => {
      loading = null;
    });
  return loading;
}

function resetAnalyticsIdentity(): void {
  if (!posthog) return;
  // A marker-free on_reject state is non-capturing. Temporarily leave that mode
  // so reset() can clear identity without logging a false consent warning, then
  // immediately restore KilnCat's current choice through the adapter.
  posthog.set_config({ cookieless_mode: undefined });
  posthog.reset();
  consentContract?.attachPostHog(createPostHogConsentAdapter(posthog));
}

function syncAnalyticsIdentity(): void {
  if (!posthog || !consentSnapshot) return;
  const sdkUserId = posthog.get_property('$user_id');
  const currentSdkUserId = typeof sdkUserId === 'string' ? sdkUserId : null;
  if (consentSnapshot.postHogMode !== 'persistent') {
    identifiedUserId = null;
    return;
  }
  if (!analyticsUserId) {
    if (identifiedUserId || currentSdkUserId) resetAnalyticsIdentity();
    identifiedUserId = null;
    return;
  }
  const nextSdkUserId = `account:${analyticsUserId}`;
  if (identifiedUserId === analyticsUserId || currentSdkUserId === nextSdkUserId) {
    identifiedUserId = analyticsUserId;
    return;
  }
  if (identifiedUserId || currentSdkUserId) resetAnalyticsIdentity();
  posthog.identify(nextSdkUserId);
  identifiedUserId = analyticsUserId;
}

/**
 * Associate accepted persistent analytics with a signed-in account.
 * Pending, rejected, and GPC/DNT states never call PostHog identify.
 */
export function setAnalyticsUser(userId: string | null | undefined): void {
  analyticsUserId = userId?.trim() || null;
  syncAnalyticsIdentity();
}

/** Start PostHog only after the synchronous first-party privacy gate permits it. */
export function initializeAnalytics(consent = window.kilncatPrivacyConsent): () => void {
  if (unsubscribeConsent) return () => {};
  consentContract = consent;
  consentSnapshot = consent.snapshot();
  unsubscribeConsent = consent.subscribe((snapshot) => void syncPostHog(snapshot));
  void syncPostHog(consentSnapshot);

  return () => {
    unsubscribeConsent?.();
    unsubscribeConsent = null;
  };
}

function sendEvent({ name, properties, sendInstantly }: ProductEvent): void {
  if (!posthog?.is_capturing()) return;
  posthog.capture(
    name,
    properties,
    sendInstantly ? { send_instantly: true, transport: 'sendBeacon' } : undefined,
  );
}

/**
 * Capture one of KilnCat's deliberately named product events.
 *
 * Returns false while GPC/DNT blocks PostHog, the SDK is not ready, or analytics
 * is disabled in a development build. Pending and rejected optional storage
 * are valid cookieless capture modes.
 */
export function captureProductEvent(
  name: string,
  properties?: Properties,
  options: { sendInstantly?: boolean } = {},
): boolean {
  if (!consentSnapshot?.canLoadPostHog || !analyticsBuildEnabled || !posthog?.is_capturing())
    return false;
  sendEvent({ name, properties, sendInstantly: options.sendInstantly });
  return true;
}
